// src/components/appointments/call/CallSurface.jsx
import React from "react";
import { useTheme } from "@/contexts/ThemeContext.jsx";

const MINI_PROFILE_SILHOUETTE = "/assets/images/profileMale-silhouette.svg";
const AVATAR_PLACEHOLDER = "/icons/circular-avatar-placeholder.svg";
const AFRIMED_WATERMARK_ICON = "/afrimed_elephant_icon.svg";

function resolveSurfaceAwareIcon(src = "", tone = "dark") {
  const iconSrc = String(src || "").trim();
  if (!iconSrc) return { src: "", className: "" };

  if (tone === "light" && iconSrc.includes("-dark.svg")) {
    return {
      src: iconSrc.replace("-dark.svg", "-light.svg"),
      className: "",
    };
  }

  if (tone === "dark" && iconSrc.includes("-light.svg")) {
    return {
      src: iconSrc.replace("-light.svg", "-dark.svg"),
      className: "",
    };
  }

  return {
    src: iconSrc,
    className: tone === "light" ? "brightness-0 invert" : "",
  };
}

/**
 * Shared shell:
 * - full-bleed fixed surface
 * - header rails (shrink / title / right actions: mobile switchCam, desktop maximize)
 * - other-caller tile
 * - background slot
 * - controls slot
 */
export default function CallSurface({
  rootRef,
  title = "",
  doctorName,
  icons = {},
  labels = {},

  // tones: "auto" | "dark" | "light"
  headerTone = "auto",
  titleTone = "auto",

  // tolerant defaults (no more crashes)
  onShrink = () => {},
  onSwitchCamera = null,
  onMaximize = () => {},

  otherThumb,
  thumbSlot,
  childrenBackground,
  childrenControls,
  toast,
  footerSlot,
}) {
  const { isDark } = useTheme();

  // Title color
  const resolvedTitleTone =
    titleTone === "auto" ? (isDark ? "light" : "dark") : titleTone;

  const titleColorClass =
    resolvedTitleTone === "light" ? "text-white" : "text-black";
  const headerTitle = title || doctorName || "";

  // Icon treatment
  // If header is on a dark bg (tone=light), we invert dark SVGs to appear white.
  const resolvedHeaderTone =
    headerTone === "auto" ? (isDark ? "light" : "dark") : headerTone;

  const shrinkIcon = resolveSurfaceAwareIcon(icons.shrink, resolvedHeaderTone);
  const switchCameraIcon = resolveSurfaceAwareIcon(icons.switchCam, resolvedHeaderTone);
  const maximizeIcon = resolveSurfaceAwareIcon(icons.maximize, resolvedHeaderTone);
  const shrinkLabel = labels.shrink || "Shrink";
  const switchCameraLabel = labels.switchCamera || "Switch camera";
  const maximizeLabel = labels.maximize || "Maximize";
  const hasRealThumb =
    Boolean(String(otherThumb || "").trim()) &&
    String(otherThumb || "").trim() !== AVATAR_PLACEHOLDER;

  return (
    <div
      ref={rootRef}
      className="fixed inset-x-0 bottom-0 top-0 md:top-14 overflow-hidden"
      data-hide-tabbar="true"
    >
      {/* Background */}
      <div className="absolute inset-0">{childrenBackground}</div>

      <div className="pointer-events-none absolute right-4 top-[calc(env(safe-area-inset-top)+18px)] z-10 md:right-8 md:top-20">
        <img
          src={AFRIMED_WATERMARK_ICON}
          alt=""
          className="h-10 w-10 opacity-50 md:h-12 md:w-12"
          draggable="false"
        />
      </div>

      {/* Header rails — blueprint width */}
      <header className="absolute left-0 right-0 top-0 z-20">
        <div
          className="afrimed-rail afrimed-rail--comfortable pb-24 pt-[calc(env(safe-area-inset-top)+28px)] md:pt-4">
          <div className="grid grid-cols-[44px_1fr_44px] items-center">
            {/* Left: Shrink */}
            <button
              type="button"
              aria-label={shrinkLabel}
              className="h-11 w-11 flex items-center justify-center opacity-90 focus:outline-none focus-visible:ring-0"
              onClick={onShrink}
            >
              <img
                src={shrinkIcon.src}
                alt=""
                className={["h-5 w-5 opacity-95", shrinkIcon.className].filter(Boolean).join(" ")}
                draggable="false"
              />
            </button>

            {/* Center: Title */}
            <div className="text-center pointer-events-none">
              <div className={`text-title-large ${titleColorClass}`}>
                {headerTitle}
              </div>
            </div>

            {/* Right: Mobile switchCam | Desktop maximize */}
            <div className="flex items-center justify-end">
              {/* Mobile: audio-only sessions do not expose a camera action. */}
              {onSwitchCamera ? <button
                type="button"
                aria-label={switchCameraLabel}
                className="h-11 w-11 flex items-center justify-center opacity-90 md:hidden focus:outline-none focus-visible:ring-0"
                onClick={onSwitchCamera}
              >
                <img
                  src={switchCameraIcon.src}
                  alt=""
                  className={["h-6 w-6", switchCameraIcon.className].filter(Boolean).join(" ")}
                  draggable="false"
                />
              </button> : null}

              {/* Desktop */}
              <button
                type="button"
                aria-label={maximizeLabel}
                className="hidden md:flex h-11 w-11 items-center justify-center opacity-90 focus:outline-none focus-visible:ring-0"
                onClick={onMaximize}
              >
                <img
                  src={maximizeIcon.src}
                  alt=""
                  className={["h-5 w-5", maximizeIcon.className].filter(Boolean).join(" ")}
                  draggable="false"
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Other caller tile (nudged DOWN) */}
      <div
        className="absolute right-8 md:right-10 z-20"
        style={{ bottom: "calc(env(safe-area-inset-bottom) + 110px)" }}
      >
        <div
          className="
            w-[100px] h-[140px] rounded-[24px]
            shadow-[0_10px_30px_rgba(0,0,0,0.22)]
            overflow-hidden
            flex items-center justify-center
            relative
          "
          style={{ background: "#D5DDEE" }}
        >
          {thumbSlot || (hasRealThumb ? (
            <img
              src={otherThumb}
              alt=""
              className="h-full w-full object-cover"
              draggable="false"
            />
          ) : (
            <img
              src={MINI_PROFILE_SILHOUETTE}
              alt=""
              draggable="false"
              className="h-full w-full object-cover"
            />
          ))}
        </div>
      </div>

      {/* Controls */}
      {childrenControls}

      {/* Optional footer slot */}
      {footerSlot ? (
        <div className="fixed left-0 right-0 bottom-[calc(env(safe-area-inset-bottom)+18px)] z-40 pointer-events-none">
          <div className="max-w-[520px] mx-auto px-4 pointer-events-auto">
            {footerSlot}
          </div>
        </div>
      ) : null}

      {/* Toast (above tray) */}
      {toast ? (
        <div className="fixed left-0 right-0 bottom-[calc(env(safe-area-inset-bottom)+92px)] z-50">
          <div className="mx-auto max-w-[520px] px-4">
            <div className="mx-auto inline-flex max-w-full rounded-full bg-[rgba(4,8,18,0.56)] px-4 py-2 text-center text-body-medium text-white shadow-[0_10px_28px_rgba(4,8,18,0.22)] backdrop-blur-md">
              <span className="mx-auto truncate">{toast}</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
